import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  ArrowUpRight, Globe, Zap, Menu, X, ArrowRight, Star, 
  Users, Trophy, School, CheckCircle2, Phone, Mail, 
  MapPin, Clock, Info, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Instagram, 
  Facebook, Linkedin, Youtube, Send, Camera, Plus,
  FileText, Layout, Sparkles, Heart, Building2,
  BookOpen, Quote, Award, Target, Image as ImageIcon,
  MessageCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";

// =============================================
// DESIGN 4 — MINIMALIST AIR
// Backbone: 100% identical to Design0_Features
// UI: Original MinimalistAir editorial design intact
// =============================================

const MinimalistAir: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {

  // ── BACKBONE STATE ────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('All');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAllStaff, setShowAllStaff] = useState(false);
  const [expandedView, setExpandedView] = useState<'staff' | 'board' | 'gallery' | 'facilities' | 'anthems' | null>(null);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // ── MINIMALIST AIR ORIGINAL STATE ─────────────────────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // ── BACKBONE EFFECTS ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const safeUrl = (file: any) => {
    if (!file) return undefined;
    if (typeof file === 'string') return file;
    try { return URL.createObjectURL(file); } catch (e) { return undefined; }
  };

  // ── BACKBONE DATA OBJECT — EXACT COPY FROM DESIGN0 ────────────────────────
  const data = {
    name: previewData?.name || "St. Augustine's National Academy",
    motto: previewData?.motto || "In Veritate et Virtute (In Truth and Virtue)",
    logo: previewData?.logo ? safeUrl(previewData.logo) : null,
    headerPhotos: (previewData?.headerPhotos && previewData.headerPhotos.filter((p: any) => p !== null).length > 0) ? previewData.headerPhotos : [
      "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=1200&auto=format&fit=crop"
    ],
    stats: {
      students: previewData?.totalStudents || "1,450",
      staff: previewData?.totalStaff || "92",
      founded: previewData?.yearFounded || "1974",
      alumni: previewData?.totalAlumni || "8,000+"
    },
    principal: {
      name: previewData?.principalName || "Dr. Richard M. Mutua",
      message: previewData?.principalMessage || "Welcome to St. Augustine's, a leading institution where we harmonize academic discipline with character development to mold the leaders of tomorrow.",
      photo: previewData?.principalPhoto ? safeUrl(previewData.principalPhoto) : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop"
    },
    mission: previewData?.mission || "To provide a holistic and inclusive learning environment that fosters academic brilliance, integrity, and global citizenship.",
    vision: previewData?.vision || "To be the premier national center of excellence, recognized globally for academic innovation and transformative leadership.",
    about: previewData?.about || "A distinguished national institution dedicated to the pursuit of excellence in all spheres of life.",
    history: previewData?.historyText || "Established in 1974 as a community initiative, our school was later elevated to National status in 2011, continuing a heritage of producing top-tier professionals and leaders for the continent.",
    aboutPhoto: previewData?.aboutPhoto ? safeUrl(previewData.aboutPhoto) : "https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?q=80&w=2070",
    missionPhoto: previewData?.missionPhoto ? safeUrl(previewData.missionPhoto) : "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070",
    visionPhoto: previewData?.visionPhoto ? safeUrl(previewData.visionPhoto) : "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?q=80&w=2070",
    whyChooseUsPhoto: previewData?.whyChooseUsPhoto ? safeUrl(previewData.whyChooseUsPhoto) : "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=2070",
    nationalAnthem: previewData?.nationalAnthem || "O God of all creation\nBless this our land and nation\nJustice be our shield and defender\nMay we dwell in unity\nPeace and liberty\nPlenty be found within our borders.\n\nLet one and all arise\nWith hearts both strong and true\nService be our earnest endeavour\nAnd our homeland of Kenya\nHeritage of splendour\nFirm may we stand to defend.\n\nLet all with one accord\nIn common bond united\nBuild this our nation together\nAnd the glory of Kenya\nThe fruit of our labour\nFill every heart with thanksgiving.",
    schoolAnthem: previewData?.schoolAnthem || "Hail to our alma mater, standing tall and true,\nGuiding lights of knowledge, red and brilliant blue.\nIn these halls of wisdom, we learn to lead and serve,\nBuilding strong foundations, with unyielding nerve.\n\nThrough the trials and triumphs, together we will rise,\nReaching for the future, beneath the open skies.\nHonor, truth, and courage, forever be our guide,\nIn our school's great legacy, we take eternal pride!",
    whyUs: previewData?.whyChooseUs || "We combine a tradition of excellence with state-of-the-art facilities and a passionate faculty dedicated to cross-disciplinary learning.",
    coreValues: previewData?.coreValues || ["Integrity", "Excellence", "Innovation", "Inclusivity", "Service"],
    quote: {
      text: previewData?.quote || "The beautiful thing about learning is that no one can take it away from you.",
      author: previewData?.quoteAuthor || "B.B. King"
    },
    curriculum: {
      description: previewData?.curriculumDesc || "Our comprehensive curriculum follows the National Competency Based framework, enhanced with international STEM integration for a global perspective.",
      compulsory: previewData?.compulsorySubjects || ["English", "Mathematics", "Kiswahili", "Chemistry", "Biology", "Physics"],
      optional: previewData?.optionalSubjects || ["Computer Studies", "French", "Business Studies", "Agriculture", "Music", "Aviation"],
      photo: previewData?.curriculumPhoto ? safeUrl(previewData.curriculumPhoto) : "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070"
    },
    activities: (previewData?.activities && previewData.activities.length > 0) ? previewData.activities : [
      { id: "1", title: "Robotics Club", description: "Design and program modern robots with Python.", photo: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=400&auto=format&fit=crop" },
      { id: "2", title: "Drama Society", description: "Annual theatrical productions and national competitions.", photo: "https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?q=80&w=400&auto=format&fit=crop" },
      { id: "3", title: "Basketball Team", description: "Our varsity team 'The Eagles' are the regional champions.", photo: "https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=400&auto=format&fit=crop" }
    ],
    facilities: (previewData?.facilities && previewData.facilities.length > 0) ? previewData.facilities : [
      { id: "1", categoryId: "Technology", title: "Modern Science Lab", description: "Fully equipped biology and chemistry laboratories.", photoFile: "https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=400&auto=format&fit=crop" },
      { id: "2", categoryId: "Academic", title: "Digital Library", description: "Access to over 10,000 e-books and research journals.", photoFile: "https://images.unsplash.com/photo-1521587760476-6c12a7b041da?q=80&w=400&auto=format&fit=crop" },
      { id: "3", categoryId: "Sports", title: "Indoor Sports Arena", description: "Olympic-sized pool and multi-purpose gymnasium.", photoFile: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=400&auto=format&fit=crop" },
      { id: "4", categoryId: "Arts", title: "Creative Arts Center", description: "Soundproof music studios and fine arts workshops.", photoFile: "https://images.unsplash.com/photo-1460518451285-8f16c4f0340b?q=80&w=400&auto=format&fit=crop" },
      { id: "5", categoryId: "Medical", title: "Student Health Clinic", description: "24/7 medical services with trained resident nurses.", photoFile: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=400&auto=format&fit=crop" },
      { id: "6", categoryId: "Residential", title: "Premium Boarding Houses", description: "Comfortable, modern living spaces with high-speed internet.", photoFile: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?q=80&w=400&auto=format&fit=crop" }
    ],
    staff: (previewData?.staffMembers && previewData.staffMembers.length > 0) ? previewData.staffMembers : [
      { id: "1", title: "Mr.", name: "Peter Otieno", bio: "HOD Sciences", photoFile: "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Ms.", name: "Sarah Wanjiku", bio: "HOD Humanities", photoFile: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Dr.", name: "David Kimani", bio: "HOD Languages", photoFile: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Mrs.", name: "Mary Atieno", bio: "HOD Mathematics", photoFile: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Mr.", name: "John Kamau", bio: "Sports Director", photoFile: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Ms.", name: "Grace Njeri", bio: "Counselor", photoFile: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=256&auto=format&fit=crop" }
    ],
    board: (previewData?.boardMembers && previewData.boardMembers.length > 0) ? previewData.boardMembers : [
      { id: "1", title: "Rev.", name: "John Mark", role: "Chairman BOM", photoFile: "https://images.unsplash.com/photo-1463453091185-61582044d556?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Dr.", name: "Alice Mwangi", role: "Treasurer", photoFile: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Hon.", name: "Samuel Korir", role: "Community Rep", photoFile: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Eng.", name: "Francis Njuguna", role: "Infrastructure", photoFile: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Prof.", name: "Jane Kemuntu", role: "Advisor", photoFile: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Lady Justice", name: "Ruth Kerubo", role: "Legal", photoFile: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=256&auto=format&fit=crop" }
    ],
    gallery: (previewData?.galleryItems && previewData.galleryItems.length > 0) ? previewData.galleryItems : [
      { id: "1", categoryId: "Campus", name: "Main Wing", description: "Our historic main administration building.", photoFile: "https://images.unsplash.com/photo-1525921429624-479b6a29d840?q=80&w=600&auto=format&fit=crop" },
      { id: "2", categoryId: "Events", name: "Sports Day", description: "Annual regional championships.", photoFile: "https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=600&auto=format&fit=crop" },
      { id: "3", categoryId: "Infrastructure", name: "Library", description: "State-of-the-art research center.", photoFile: "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=600&auto=format&fit=crop" },
      { id: "4", categoryId: "Students", name: "Tech Club", description: "Students building a robotics prototype.", photoFile: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=600&auto=format&fit=crop" },
      { id: "5", categoryId: "Trips", name: "Mountain Hike", description: "Biology class exploring Mount Kenya flora.", photoFile: "https://images.unsplash.com/photo-1503220317375-aaad61436b1b?q=80&w=600&auto=format&fit=crop" }
    ],
    contact: {
      phones: previewData?.phones || ["+254 712 345 678", "+254 789 012 345"],
      email: previewData?.email || "info@staugustines.ac.ke",
      whatsapp: previewData?.whatsapp || "254712345678",
      facebook: previewData?.fbLink || "",
      twitter: previewData?.twitterLink || "",
      instagram: previewData?.instaLink || "",
      address: previewData?.address || "Main Campus, Kisumu-Kakamega Road, Kisumu",
      mapsLink: previewData?.mapsLink || ""
    },
    admissions: {
      process: previewData?.admissionProcess || "Our intake happens annually in January. Prospective students must submit their applications through our online portal or in-person at the Registrar's Office.",
      requirements: previewData?.admissionRequirements || ["Certified Birth Certificate", "KCPE Result Slip", "Leaving Certificate", "Medical Examination Report"],
      formFile: previewData?.admissionFormFile || null
    },
    payments: (previewData?.paymentMethods && previewData.paymentMethods.length > 0) ? previewData.paymentMethods : [
      { id: "1", method: "M-Pesa Paybill", details: "Business No: 123456 | Account: Student Name" },
      { id: "2", method: "KCB Bank", details: "Acc Name: St. Augustine's | Acc No: 0987654321" }
    ],
    feePolicy: previewData?.feeBreakdown || "School fees are due on or before the first day of each term. Payment plans are available upon request through the Finance Office."
  };

  // ── BACKBONE EFFECTS CONTINUED ────────────────────────────────────────────
  useEffect(() => {
    if (data.headerPhotos.length <= 1) return;
    const interval = setInterval(() => setCurrentSlide(prev => (prev + 1) % data.headerPhotos.length), 5000);
    return () => clearInterval(interval);
  }, [data.headerPhotos.length]);

  const filteredGallery = data.gallery.filter((item: any) => activeTab === 'All' || (item.categoryId || item.category) === activeTab);

  // ── MINIMALIST AIR THEME EFFECT ───────────────────────────────────────────
  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
      const { colors, fonts } = previewData.theme;
      root.style.setProperty('--color-primary', colors.primary || '#000000');
      root.style.setProperty('--color-secondary', colors.secondary || '#3b82f6');
      root.style.setProperty('--color-accent', colors.accent || '#f3f4f6');
      root.style.setProperty('--color-background', colors.background || '#ffffff');
      root.style.setProperty('--color-text', colors.text || '#000000');
      root.style.setProperty('--font-heading', `'${fonts.heading || 'Inter'}', sans-serif`);
      root.style.setProperty('--font-body', `'${fonts.body || 'Inter'}', sans-serif`);
      const radiusMap: any = { none: '0px', small: '2px', medium: '6px', large: '12px', full: '9999px' };
      root.style.setProperty('--radius', radiusMap[previewData.theme.borderRadius || 'none']);
    }
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);

  const nav = [
    { label: 'About', href: '#about' },
    { label: 'Academics', href: '#academics' },
    { label: 'Facilities', href: '#facilities' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Faculty', href: '#staff' },
    { label: 'Admissions', href: '#admissions' }
  ];

  return (
    <div ref={containerRef} className="minimalist-air bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-sans selection:bg-black selection:text-white">

      {/* ── NAV (MinimalistAir design) ── */}
      <nav className={`fixed top-0 w-full z-50 p-5 md:p-7 flex justify-between items-center transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm' : 'bg-white/80 backdrop-blur-md border-b border-gray-100'}`}>
        <div className="flex items-center gap-4">
          <div className="w-9 h-9 overflow-hidden flex items-center justify-center grayscale">
            {data.logo ? <img src={data.logo} alt="Logo" className="w-full h-full object-contain" /> : <School size={22} />}
          </div>
          <div className="text-base font-bold tracking-tighter uppercase leading-none">{data.name}.</div>
        </div>

        <div className="hidden lg:flex items-center gap-10">
          <div className="flex gap-8 text-[10px] font-bold uppercase tracking-[0.2em]">
            {nav.map((n, i) => (
              <a key={i} href={n.href} className="hover:text-[var(--color-secondary)] transition-colors opacity-60 hover:opacity-100">{n.label}</a>
            ))}
          </div>
          <div className="flex items-center gap-3 relative">
            <button onClick={() => setShowLoginDropdown(!showLoginDropdown)} className="text-[10px] font-bold uppercase tracking-[0.2em] border border-black/10 px-5 py-2 rounded-full hover:bg-black hover:text-white transition-all flex items-center gap-2">
              Portals <ChevronDown size={11} className={showLoginDropdown ? 'rotate-180' : ''} />
            </button>
            {showLoginDropdown && (
              <div className="absolute top-full right-0 mt-3 w-44 bg-white border border-gray-100 shadow-2xl p-1.5 z-[60]">
                <a href="#" className="flex items-center gap-3 p-3 text-[10px] font-bold uppercase tracking-widest hover:bg-gray-50 transition-colors"><Heart size={13} className="text-gray-400" /> Parent</a>
                <a href="#" className="flex items-center gap-3 p-3 text-[10px] font-bold uppercase tracking-widest hover:bg-gray-50 transition-colors"><Star size={13} className="text-gray-400" /> Student</a>
              </div>
            )}
          </div>
        </div>

        <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* ── HERO — Split Screen (MinimalistAir design, backbone slideshow) ── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-screen pt-20">
        <div className="relative h-[55vh] lg:h-auto overflow-hidden bg-gray-50">
          {data.headerPhotos.map((p: any, i: number) => (
            <img key={i} src={safeUrl(p)} alt={`Hero ${i}`} className={`absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-[2000ms] ${i === currentSlide ? 'opacity-100' : 'opacity-0'}`} />
          ))}
          <div className="absolute bottom-10 left-10 p-8 bg-white/10 backdrop-blur-xl border border-white/20 text-white max-w-sm hidden md:block">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase mb-3 opacity-70">Est. {data.stats.founded}</div>
            <h2 className="text-xl font-light italic leading-tight">"{data.motto}"</h2>
          </div>
          {/* Slide dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {data.headerPhotos.map((_: any, i: number) => (
              <button key={i} onClick={() => setCurrentSlide(i)} className={`w-6 h-[2px] transition-all bg-white ${i === currentSlide ? 'opacity-100' : 'opacity-30'}`} aria-label={`Go to slide ${i + 1}`} title={`Go to slide ${i + 1}`}><span className="sr-only">Go to slide {i + 1}</span></button>
            ))}
          </div>
        </div>
        <div className="flex flex-col justify-center p-10 lg:p-28 bg-white">
          <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 mb-8 uppercase">01 // INTRODUCTION</span>
          <h1 className="text-4xl md:text-7xl font-medium tracking-tighter leading-[0.9] mb-10">
            <span className="text-[var(--color-secondary)] italic">{data.name}</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-500 max-w-md font-light leading-snug mb-12 italic">"{data.motto}"</p>
          <div className="flex flex-col sm:flex-row gap-6">
            <a href="#about" className="flex items-center justify-between w-full sm:w-56 gap-2 text-[10px] font-black uppercase tracking-[0.3em] border-b border-black pb-3 hover:text-[var(--color-secondary)] hover:border-[var(--color-secondary)] transition-all group">
              Our History <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <a href="#admissions" className="flex items-center justify-between w-full sm:w-56 gap-2 text-[10px] font-black uppercase tracking-[0.3em] border-b border-black/10 pb-3 hover:border-black transition-all">
              Admissions <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ── STATS (MinimalistAir design, backbone data) ── */}
      <section className="py-28 px-10 border-t border-gray-100 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 items-start">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">02 // BY THE NUMBERS</span>
            </div>
            <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-12 lg:gap-24">
              {[
                { value: data.stats.students, label: "Students" },
                { value: data.stats.staff, label: "Staff" },
                { value: data.stats.founded, label: "Founded" },
                { value: data.stats.alumni, label: "Alumni" }
              ].map((stat, i) => (
                <div key={i} className="group">
                  <div className="text-5xl md:text-7xl font-light tracking-tighter mb-3 group-hover:italic transition-all">{stat.value}</div>
                  <div className="text-[10px] font-bold tracking-[0.3em] text-gray-400 uppercase">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ABOUT / HISTORY (MinimalistAir editorial layout, backbone data) ── */}
      <section id="about" className="py-36 px-6 bg-[var(--color-accent)]">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
            <div className="lg:col-span-7">
              <div className="relative group">
                <img src={data.aboutPhoto} className="w-full aspect-[4/5] object-cover grayscale group-hover:grayscale-0 transition-all duration-1000" alt="History" />
                <div className="absolute top-0 right-0 w-28 h-28 bg-white flex items-center justify-center -translate-y-1/2 translate-x-1/2 shadow-2xl rotate-12">
                  <Globe size={40} className="text-[var(--color-secondary)]" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 mb-8 block uppercase">03 // OUR HISTORY</span>
              <h2 className="text-4xl md:text-6xl font-medium tracking-tighter leading-tight mb-10">{data.name}</h2>
              <p className="text-xl md:text-2xl font-light text-gray-500 leading-relaxed mb-12 italic">{data.history}</p>
              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="p-6 border border-black/5 bg-white">
                  <div className="text-sm font-bold tracking-[0.2em] uppercase mb-3">Vision</div>
                  <p className="text-sm text-gray-500 leading-relaxed">{data.vision}</p>
                </div>
                <div className="p-6 border border-black/5 bg-white">
                  <div className="text-sm font-bold tracking-[0.2em] uppercase mb-3">Mission</div>
                  <p className="text-sm text-gray-500 leading-relaxed">{data.mission}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US & ANTHEMS (MinimalistAir design, backbone data) ── */}
      <section className="py-36 px-6 bg-white border-y border-gray-100">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 items-start mb-24">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">04 // DISTINCTION</span>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-5xl md:text-8xl font-medium tracking-tighter mb-10 leading-none">Why Choose Us.</h2>
              <p className="text-xl text-gray-500 font-light italic max-w-3xl">{data.whyUs}</p>
            </div>
          </div>

          {/* Core Values in MinimalistAir style */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-24">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">Core Values</span>
            </div>
            <div className="lg:col-span-3 flex flex-wrap gap-3">
              {data.coreValues.map((v: string, i: number) => (
                <span key={i} className="border border-black/10 px-5 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all cursor-default">{v}</span>
              ))}
            </div>
          </div>

          {/* Anthems in MinimalistAir style */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">Anthems</span>
            </div>
            <div className="lg:col-span-3 grid sm:grid-cols-2 gap-6">
              <div
                onClick={() => setExpandedView('anthems')}
                className="group border border-black/5 bg-[var(--color-accent)] p-10 cursor-crosshair hover:border-black hover:bg-black hover:text-white transition-all duration-500"
              >
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em]">National Anthem</span>
                  <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
                <p className="text-sm font-light italic leading-relaxed text-gray-500 group-hover:text-gray-300 line-clamp-2">{data.nationalAnthem}</p>
              </div>
              <div
                onClick={() => setExpandedView('anthems')}
                className="group border border-black/5 bg-[var(--color-accent)] p-10 cursor-crosshair hover:border-black hover:bg-black hover:text-white transition-all duration-500"
              >
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em]">School Anthem</span>
                  <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
                <p className="text-sm font-light italic leading-relaxed text-gray-500 group-hover:text-gray-300 line-clamp-2">{data.schoolAnthem}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ACADEMICS / CURRICULUM (MinimalistAir design, backbone data) ── */}
      <section id="academics" className="py-36 px-6 bg-[var(--color-accent)]">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-24">
            <div className="max-w-xl">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 mb-6 block uppercase">05 // CURRICULUM</span>
              <h2 className="text-5xl md:text-8xl font-medium tracking-tighter">Academic Program.</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="relative group">
              <img src={data.curriculum.photo} className="w-full aspect-[4/3] object-cover grayscale group-hover:grayscale-0 transition-all duration-1000" alt="Curriculum" />
            </div>
            <div className="space-y-8">
              <p className="text-xl text-gray-500 font-light italic leading-relaxed">{data.curriculum.description}</p>
              <div className="bg-white p-10 border border-black/5">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-6 border-b border-black/5 pb-4 flex items-center gap-3"><BookOpen size={14} /> Core Subjects</div>
                <div className="flex flex-wrap gap-2">
                  {data.curriculum.compulsory.map((s: string, i: number) => (
                    <span key={i} className="border border-black/10 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-all">{s}</span>
                  ))}
                </div>
              </div>
              <div className="bg-black text-white p-10">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-6 border-b border-white/10 pb-4 text-gray-400">Elective Subjects</div>
                <div className="flex flex-wrap gap-2">
                  {data.curriculum.optional.map((s: string, i: number) => (
                    <span key={i} className="border border-white/20 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-all">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CLUBS & ACTIVITIES (MinimalistAir grid, backbone data) ── */}
      <section className="py-36 px-6 bg-white border-t border-gray-100">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 items-start mb-24">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">06 // CO-CURRICULAR</span>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-5xl md:text-8xl font-medium tracking-tighter leading-none">Activities.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-black/5">
            {data.activities.map((a: any, i: number) => (
              <div key={i} className="border-r border-b border-black/5 p-10 md:p-14 hover:bg-gray-50 transition-colors group">
                <div className="mb-10 aspect-[4/5] overflow-hidden grayscale group-hover:grayscale-0 transition-all duration-700">
                  {a.photo ? <img src={safeUrl(a.photo)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Activity" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><Trophy size={32} className="text-gray-300" /></div>}
                </div>
                <h3 className="text-2xl font-medium tracking-tighter italic mb-4">{a.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{a.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FACILITIES (MinimalistAir design, backbone data + View More) ── */}
      <section id="facilities" className="py-36 px-6 bg-[var(--color-accent)]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 items-start mb-24">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">07 // ENVIRONMENT</span>
            </div>
            <div className="lg:col-span-2">
              <h2 className="text-5xl md:text-7xl font-medium tracking-tighter">Designed for Focus.</h2>
            </div>
            <div className="lg:col-span-1 text-right">
              <div className="inline-flex items-center gap-3">
                <button className="w-11 h-11 border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-all" aria-label="Previous facility" title="Previous facility"><span className="sr-only">Previous facility</span><ChevronLeft size={15} /></button>
                <button className="w-11 h-11 border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-all" aria-label="Next facility" title="Next facility"><span className="sr-only">Next facility</span><ChevronRight size={15} /></button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {data.facilities.slice(0, 4).map((fac: any, i: number) => (
              <div key={i} className="group relative bg-white p-1 shadow-sm hover:shadow-2xl transition-all duration-500">
                <div className="relative aspect-video overflow-hidden">
                  {fac.photoFile ? <img src={safeUrl(fac.photoFile)} alt={fac.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><Building2 size={40} className="text-gray-300" /></div>}
                  <div className="absolute top-6 right-6 bg-white/20 backdrop-blur-3xl border border-white/30 px-3 py-1.5 text-[9px] font-mono text-white uppercase tracking-widest">{fac.categoryId}</div>
                </div>
                <div className="p-10">
                  <div className="flex justify-between items-start gap-4 mb-3">
                    <h3 className="text-2xl font-medium tracking-tighter italic">{fac.title}</h3>
                    <span className="text-[10px] font-mono opacity-30">FACILITY // {String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <p className="text-gray-500 font-light leading-relaxed">{fac.description}</p>
                </div>
              </div>
            ))}
          </div>

          {data.facilities.length > 4 && (
            <div className="mt-16 text-center">
              <button
                onClick={() => setExpandedView('facilities')}
                className="text-[10px] font-bold uppercase tracking-[0.3em] border border-black px-12 py-4 hover:bg-black hover:text-white transition-all inline-flex items-center gap-3"
              >
                View All Facilities <ArrowUpRight size={14} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── PRINCIPAL (MinimalistAir list-row style, backbone data) ── */}
      <section className="py-36 px-6 bg-white border-t border-gray-100">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 items-start mb-24">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">08 // PRINCIPAL'S ADDRESS</span>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-5xl md:text-8xl font-medium tracking-tighter leading-none italic">Leadership.</h2>
            </div>
          </div>

          <div className="border-t border-black/10">
            <div className="group border-b border-black/10 flex flex-col md:flex-row justify-between items-start md:items-center py-16 px-8 hover:bg-[var(--color-accent)] transition-all duration-500 overflow-hidden">
              <div className="relative z-10">
                <h3 className="text-4xl md:text-7xl font-medium tracking-tighter mb-4 group-hover:translate-x-4 transition-transform duration-500">{data.principal.name}</h3>
                <p className="text-[10px] font-black uppercase tracking-[0.5em] text-[var(--color-secondary)]">Chief Principal</p>
                <p className="text-lg font-light text-gray-500 italic mt-6 max-w-xl">"{data.principal.message}"</p>
              </div>
              <div className="md:w-52 aspect-[3/4] overflow-hidden grayscale group-hover:grayscale-0 transition-all duration-700 md:opacity-0 group-hover:opacity-100 mt-10 md:mt-0 relative z-10 translate-x-16 group-hover:translate-x-0">
                {data.principal.photo ? <img src={safeUrl(data.principal.photo)} className="w-full h-full object-cover" alt="Principal" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><Users size={32} className="text-gray-300" /></div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STAFF & BOARD (MinimalistAir list style, backbone data + View More) ── */}
      <section id="staff" className="py-36 px-6 bg-[var(--color-accent)]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 items-start mb-24">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">09 // LEADERSHIP</span>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-5xl md:text-8xl font-medium tracking-tighter leading-none italic">Our People.</h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Faculty */}
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-400 mb-8 border-b border-black/10 pb-4">Academic Faculty</h4>
              <div className="flex flex-col border-t border-black/10">
                {data.staff.slice(0, 4).map((s: any, i: number) => (
                  <div key={i} className="group border-b border-black/10 flex justify-between items-center py-8 px-4 hover:bg-white transition-all duration-300">
                    <div>
                      <h3 className="text-xl font-medium tracking-tighter group-hover:translate-x-3 transition-transform">{s.title} {s.name}</h3>
                      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--color-secondary)] mt-1">{s.bio || 'Faculty'}</p>
                    </div>
                    <div className="w-14 aspect-square overflow-hidden grayscale group-hover:grayscale-0 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-x-4 group-hover:translate-x-0">
                      {s.photoFile ? <img src={safeUrl(s.photoFile)} className="w-full h-full object-cover" alt={s.name} /> : <div className="w-full h-full bg-gray-100" />}
                    </div>
                  </div>
                ))}
              </div>
              {data.staff.length > 4 && (
                <div className="mt-10">
                  <button
                    onClick={() => setExpandedView('staff')}
                    className="text-[10px] font-bold uppercase tracking-[0.3em] border-b border-black pb-2 hover:text-[var(--color-secondary)] hover:border-[var(--color-secondary)] transition-all flex items-center gap-2"
                  >
                    View All Faculty <ArrowUpRight size={13} />
                  </button>
                </div>
              )}
            </div>

            {/* Board */}
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-400 mb-8 border-b border-black/10 pb-4">Board of Management</h4>
              <div className="flex flex-col border-t border-black/10">
                {data.board.slice(0, 4).map((b: any, i: number) => (
                  <div key={i} className="group border-b border-black/10 flex justify-between items-center py-8 px-4 hover:bg-white transition-all duration-300">
                    <div>
                      <h3 className="text-xl font-medium tracking-tighter group-hover:translate-x-3 transition-transform">{b.title} {b.name}</h3>
                      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--color-secondary)] mt-1">{b.role}</p>
                    </div>
                    <div className="w-14 aspect-square overflow-hidden grayscale group-hover:grayscale-0 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-x-4 group-hover:translate-x-0">
                      {b.photoFile ? <img src={safeUrl(b.photoFile)} className="w-full h-full object-cover" alt={b.name} /> : <div className="w-full h-full bg-gray-100" />}
                    </div>
                  </div>
                ))}
              </div>
              {data.board.length > 4 && (
                <div className="mt-10">
                  <button
                    onClick={() => setExpandedView('board')}
                    className="text-[10px] font-bold uppercase tracking-[0.3em] border-b border-black pb-2 hover:text-[var(--color-secondary)] hover:border-[var(--color-secondary)] transition-all flex items-center gap-2"
                  >
                    View Full Board <ArrowUpRight size={13} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── GALLERY (MinimalistAir masonry design, backbone data + View More) ── */}
      <section id="gallery" className="py-36 px-6 bg-white border-t border-gray-100 overflow-hidden">
        <div className="container mx-auto">
          <div className="text-center mb-28">
            <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 mb-8 block uppercase">10 // VISUAL JOURNAL</span>
            <h2 className="text-6xl md:text-9xl font-medium tracking-tighter leading-none mb-12">
              Captured <br /><span className="italic">Moments.</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-8 border-y border-black/5 py-7">
              {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                <button
                  key={cat}
                  onClick={() => setActiveTab(cat)}
                  className={`text-[10px] font-bold uppercase tracking-[0.3em] transition-all relative pb-2 ${activeTab === cat ? 'text-[var(--color-secondary)]' : 'text-gray-400 hover:text-black'}`}
                >
                  {cat}
                  {activeTab === cat && <div className="absolute -bottom-0.5 left-0 w-full h-[2px] bg-[var(--color-secondary)]" />}
                </button>
              ))}
            </div>
          </div>

          <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
            {filteredGallery.slice(0, 4).map((item: any, i: number) => (
              <div
                key={i}
                onClick={() => setLightboxIndex(i)}
                className="break-inside-avoid relative group cursor-crosshair border border-black/5"
              >
                {item.photoFile || item.photo ? (
                  <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Gallery"} className="w-full grayscale group-hover:grayscale-0 transition-all duration-1000" />
                ) : (
                  <div className="w-full aspect-video bg-gray-100 flex items-center justify-center"><ImageIcon size={32} className="text-gray-300" /></div>
                )}
                <div className="p-6 bg-white flex justify-between items-center group-hover:bg-black group-hover:text-white transition-all">
                  <div className="text-[10px] font-bold tracking-widest uppercase italic">{item.name}</div>
                  <span className="text-[10px] font-mono opacity-20 group-hover:opacity-50 tracking-tighter">REF: {String(i).padStart(3, '0')}</span>
                </div>
              </div>
            ))}
          </div>

          {filteredGallery.length > 4 && (
            <div className="mt-20 text-center">
              <button
                onClick={() => setExpandedView('gallery')}
                className="text-[10px] font-bold uppercase tracking-[0.3em] border border-black px-14 py-5 hover:bg-black hover:text-white transition-all inline-flex items-center gap-3"
              >
                Expand Visual Journal <ArrowUpRight size={14} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── ADMISSIONS & FEES (MinimalistAir design, backbone data) ── */}
      <section id="admissions" className="py-36 px-6 bg-[var(--color-accent)]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 items-start mb-24">
            <div className="lg:col-span-1">
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-400 block uppercase">11 // ADMISSIONS</span>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-5xl md:text-8xl font-medium tracking-tighter leading-none">Enrollment.</h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-20">
            <div className="space-y-8">
              <div className="bg-white p-10 border border-black/5">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-5 border-b border-black/5 pb-4">Process Overview</div>
                <p className="text-lg font-light text-gray-500 italic leading-relaxed border-l-4 border-[var(--color-secondary)] pl-6">{data.admissions.process}</p>
              </div>
              <div className="bg-white p-10 border border-black/5">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-5 border-b border-black/5 pb-4">Requirements</div>
                <ul className="space-y-4">
                  {data.admissions.requirements.map((req: string, i: number) => (
                    <li key={i} className="flex items-center gap-4 text-sm font-light text-gray-600">
                      <div className="w-4 h-[1px] bg-[var(--color-secondary)]" /> {req}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="space-y-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-400 mb-6">Fee Structure</div>
              {data.payments.map((p: any, i: number) => (
                <div key={i} className="group bg-white p-8 flex justify-between items-center border border-black/5 hover:border-black transition-all">
                  <div>
                    <p className="text-[10px] font-bold text-[var(--color-secondary)] uppercase tracking-widest mb-1">{p.method}</p>
                    <p className="font-medium">{p.details}</p>
                  </div>
                  <ArrowUpRight size={18} className="text-gray-200 group-hover:text-black transition-colors" />
                </div>
              ))}
              <div className="bg-white p-8 border-l-4 border-[var(--color-secondary)]">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Policy</p>
                <p className="text-sm font-light italic text-gray-500">"{data.feePolicy}"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── QUOTE & CORE VALUES (MinimalistAir style, backbone data) ── */}
      <section className="py-36 px-6 bg-black text-white">
        <div className="container mx-auto max-w-5xl text-center">
          <div className="w-20 h-20 bg-[var(--color-secondary)] rounded-full mx-auto mb-16 flex items-center justify-center">
            <Quote size={36} className="text-white" />
          </div>
          <h2 className="text-4xl md:text-7xl font-medium tracking-tighter leading-none mb-10 italic">"{data.quote.text}"</h2>
          <p className="text-[10px] font-bold uppercase tracking-[0.5em] text-gray-500 mb-20">— {data.quote.author}</p>
          <div className="flex flex-wrap justify-center gap-4">
            {data.coreValues.map((v: string, i: number) => (
              <span key={i} className="border border-white/20 px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all cursor-default">{v}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER (MinimalistAir extreme minimalism, backbone data) ── */}
      <footer id="contact" className="bg-white pt-36 pb-16 px-6 border-t border-gray-100">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-20 lg:gap-8 mb-32">
            <div className="lg:col-span-1">
              <div className="w-10 h-10 bg-black flex items-center justify-center font-bold text-white shrink-0 mb-4">
                {data.logo ? <img src={data.logo} alt="School Logo" className="h-full w-full object-contain p-1" /> : data.name.charAt(0)}
              </div>
              <div className="text-base font-bold tracking-tighter uppercase mb-4 leading-tight">{data.name}.</div>
              <p className="text-sm text-gray-400 leading-relaxed italic max-w-xs mb-6">"{data.motto}"</p>
              {data.contact.whatsapp && (
                <a href={`https://wa.me/${data.contact.whatsapp}`} target="_blank" className="text-[10px] font-bold uppercase tracking-widest border border-black/10 px-5 py-2.5 inline-flex items-center gap-2 hover:bg-black hover:text-white hover:border-black transition-all">
                  <MessageCircle size={13} /> WhatsApp
                </a>
              )}
            </div>

            <div>
              <h4 className="text-[10px] font-bold tracking-[0.4em] uppercase text-gray-400 mb-8">Core Links</h4>
              <ul className="space-y-5 text-[10px] font-bold uppercase tracking-[0.2em]">
                {nav.map((item, i) => (
                  <li key={i}><a href={item.href} className="hover:text-[var(--color-secondary)] transition-all">{item.label}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-bold tracking-[0.4em] uppercase text-gray-400 mb-8">Connect</h4>
              <ul className="space-y-7">
                <li className="flex gap-5 group">
                  <MapPin size={16} className="text-gray-300 group-hover:text-black transition-colors shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-mono text-gray-300 uppercase mb-1">Location</p>
                    <p className="text-xs font-bold leading-relaxed">{data.contact.address}</p>
                  </div>
                </li>
                {data.contact.phones.map((p: string, i: number) => (
                  <li key={i} className="flex gap-5 group">
                    <Phone size={16} className="text-gray-300 group-hover:text-black transition-colors shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] font-mono text-gray-300 uppercase mb-1">Line {i + 1}</p>
                      <p className="text-xs font-bold">{p}</p>
                    </div>
                  </li>
                ))}
                <li className="flex gap-5 group">
                  <Mail size={16} className="text-gray-300 group-hover:text-black transition-colors shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-mono text-gray-300 uppercase mb-1">Email</p>
                    <p className="text-xs font-bold">{data.contact.email}</p>
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-bold tracking-[0.4em] uppercase text-gray-400 mb-8">Location Map</h4>
              <div className="aspect-[4/3] bg-gray-50 border border-black/5 overflow-hidden relative">
                {data.contact.mapsLink ? (
                  <iframe title="School map" src={data.contact.mapsLink} className="w-full h-full border-none grayscale pointer-events-none" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs italic">Map not configured</div>
                )}
                {data.contact.mapsLink && (
                  <a href={data.contact.mapsLink} aria-label="Open map" className="absolute bottom-2 right-2 bg-black text-white p-1.5"><ArrowUpRight size={13} /></a>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-10 pt-16 border-t border-black/5 opacity-30">
            <div className="flex gap-10">
              {data.contact.facebook && <a href={data.contact.facebook} aria-label="Facebook"><Facebook size={13} /></a>}
              {data.contact.instagram && <a href={data.contact.instagram} aria-label="Instagram"><Instagram size={13} /></a>}
            </div>
            <div className="text-[8px] font-bold uppercase tracking-[0.5em] text-center">
              &copy; {new Date().getFullYear()} {data.name} // ALL RIGHTS RESERVED
            </div>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-[8px] font-bold uppercase tracking-[0.5em] border-b border-black/30 pb-1 hover:opacity-100 transition-opacity">↑ Top</button>
          </div>
        </div>
      </footer>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-[100] bg-white p-10 flex flex-col justify-between">
          <div className="flex justify-between items-center">
            <div className="text-base font-bold tracking-tighter uppercase leading-tight">{data.name}.</div>
            <button onClick={() => setMenuOpen(false)} aria-label="Close menu" title="Close menu"><span className="sr-only">Close menu</span><X size={28} /></button>
          </div>
          <nav className="flex flex-col gap-6">
            {nav.map((n, i) => (
              <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-5xl font-medium tracking-tighter italic hover:text-[var(--color-secondary)] transition-all leading-none">{n.label}</a>
            ))}
          </nav>
          <div className="border-t border-black/5 pt-10 flex justify-between items-end">
            <div className="text-[10px] font-bold uppercase tracking-[0.4em]">Est. {data.stats.founded}</div>
            <a href="#contact" onClick={() => setMenuOpen(false)} className="bg-black text-white px-10 py-4 text-[10px] font-bold uppercase tracking-[0.4em] hover:bg-gray-800 transition-all">Contact</a>
          </div>
        </div>
      )}

      {/* ── EXPANDED DIRECTORY OVERLAY (backbone logic, MinimalistAir style) ── */}
      {expandedView && (
        <div className="fixed inset-0 z-[200] bg-white overflow-y-auto pb-24">
          <div className="border-b border-gray-100 px-8 py-5 flex items-center justify-between sticky top-0 z-50 bg-white/95 backdrop-blur-sm">
            <div className="flex items-center gap-5">
              <button onClick={() => setExpandedView(null)} className="w-10 h-10 border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-all" aria-label="Go back" title="Go back">
                <span className="sr-only">Go back</span>
                <ChevronLeft size={18} />
              </button>
              <div>
                <h2 className="text-lg font-medium tracking-tighter italic">
                  {expandedView === 'staff' ? 'Academic Faculty' : expandedView === 'gallery' ? 'Visual Journal' : expandedView === 'facilities' ? 'All Facilities' : expandedView === 'anthems' ? 'Institutional Anthems' : 'Board of Management'}
                </h2>
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400 mt-0.5">{data.name}</p>
              </div>
            </div>
            <button onClick={() => setExpandedView(null)} className="text-gray-400 hover:text-black p-2" aria-label="Close overlay" title="Close overlay"><span className="sr-only">Close overlay</span><X size={20} /></button>
          </div>

          <div className="max-w-7xl mx-auto px-8 py-16">
            {expandedView === 'gallery' ? (
              <>
                <div className="flex flex-wrap justify-center gap-8 border-y border-black/5 py-7 mb-16">
                  {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                    <button
                      key={cat}
                      onClick={() => setActiveTab(cat)}
                      className={`text-[10px] font-bold uppercase tracking-[0.3em] transition-all relative pb-2 ${activeTab === cat ? 'text-[var(--color-secondary)]' : 'text-gray-400 hover:text-black'}`}
                    >
                      {cat}
                      {activeTab === cat && <div className="absolute -bottom-0.5 left-0 w-full h-[2px] bg-[var(--color-secondary)]" />}
                    </button>
                  ))}
                </div>
                <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
                  {filteredGallery.map((item: any, i: number) => (
                    <div key={i} onClick={() => setLightboxIndex(i)} className="break-inside-avoid relative group cursor-crosshair border border-black/5">
                      {item.photoFile || item.photo ? <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Gallery"} className="w-full grayscale group-hover:grayscale-0 transition-all duration-1000" /> : <div className="w-full aspect-video bg-gray-100 flex items-center justify-center"><ImageIcon size={32} className="text-gray-300" /></div>}
                      <div className="p-5 bg-white flex justify-between items-center group-hover:bg-black group-hover:text-white transition-all">
                        <div className="text-[10px] font-bold tracking-widest uppercase italic">{item.name}</div>
                        <span className="text-[10px] font-mono opacity-20 group-hover:opacity-50 tracking-tighter">REF: {String(i).padStart(3, '0')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : expandedView === 'facilities' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {data.facilities.map((fac: any, i: number) => (
                  <div key={i} className="group bg-white border border-black/5 hover:shadow-xl transition-all">
                    <div className="relative aspect-video overflow-hidden">
                      {fac.photoFile ? <img src={safeUrl(fac.photoFile)} alt={fac.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><Building2 size={40} className="text-gray-300" /></div>}
                      <div className="absolute top-4 right-4 bg-white/20 backdrop-blur px-3 py-1 text-[9px] font-mono text-white uppercase">{fac.categoryId}</div>
                    </div>
                    <div className="p-10">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-2xl font-medium tracking-tighter italic">{fac.title}</h3>
                        <span className="text-[10px] font-mono opacity-30">FACILITY // {String(i + 1).padStart(2, '0')}</span>
                      </div>
                      <p className="text-gray-500 font-light leading-relaxed">{fac.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : expandedView === 'anthems' ? (
              <div className="max-w-4xl mx-auto space-y-24">
                <div className="space-y-12">
                  <div className="text-center">
                    <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-secondary)]">The Republic of Kenya</span>
                    <h3 className="text-4xl md:text-6xl font-medium tracking-tighter italic mt-4">National Anthem</h3>
                    <div className="w-16 h-[2px] bg-[var(--color-secondary)] mx-auto mt-6" />
                  </div>
                  <div className="aspect-video overflow-hidden relative">
                    <img src="https://images.unsplash.com/photo-1608925565154-2974eb36e6ba?q=80&w=2070" className="w-full h-full object-cover grayscale" alt="National Anthem" />
                    <div className="absolute inset-0 bg-black/30" />
                  </div>
                  <div className="bg-[var(--color-accent)] p-12 md:p-16 border border-black/5 text-center">
                    <p className="whitespace-pre-line text-lg text-gray-600 leading-loose italic font-light">{data.nationalAnthem}</p>
                  </div>
                </div>
                <div className="space-y-12 pt-16 border-t border-black/10">
                  <div className="text-center">
                    <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-secondary)]">{data.name}</span>
                    <h3 className="text-4xl md:text-6xl font-medium tracking-tighter italic mt-4">School Anthem</h3>
                    <div className="w-16 h-[2px] bg-[var(--color-secondary)] mx-auto mt-6" />
                  </div>
                  <div className="aspect-video overflow-hidden relative">
                    <img src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070" className="w-full h-full object-cover grayscale" alt="School Anthem" />
                    <div className="absolute inset-0 bg-black/30" />
                  </div>
                  <div className="bg-[var(--color-accent)] p-12 md:p-16 border border-black/5 text-center">
                    <p className="whitespace-pre-line text-lg text-gray-600 leading-loose italic font-light">{data.schoolAnthem}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col border-t border-black/10">
                {(expandedView === 'staff' ? data.staff : data.board).map((item: any, i: number) => (
                  <div key={i} className="group border-b border-black/10 flex justify-between items-center py-10 px-6 hover:bg-[var(--color-accent)] transition-all duration-300">
                    <div>
                      <h3 className="text-2xl font-medium tracking-tighter group-hover:translate-x-4 transition-transform">{item.title} {item.name}</h3>
                      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--color-secondary)] mt-1.5">{expandedView === 'staff' ? (item.bio || 'Faculty') : item.role}</p>
                    </div>
                    <div className="w-16 h-20 overflow-hidden grayscale group-hover:grayscale-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                      {(item.photoFile || item.photo) ? <img src={safeUrl(item.photoFile || item.photo)} className="w-full h-full object-cover" alt={item.name} /> : <div className="w-full h-full bg-gray-100" />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LIGHTBOX (backbone logic, MinimalistAir style) ── */}
      {lightboxIndex !== null && filteredGallery[lightboxIndex] && (
        <div className="fixed inset-0 bg-white flex items-center justify-center" style={{ zIndex: 99999 }}>
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-6 left-6 z-50 text-black/50 transition-all flex items-center gap-2 font-bold uppercase tracking-[0.3em] text-[10px] border border-black/10 px-5 py-2.5 hover:bg-black hover:text-white"
          >
            <ChevronLeft size={13} /> Close
          </button>
          <div className="absolute inset-0 w-full h-full bg-[var(--color-accent)]">
            {(filteredGallery[lightboxIndex] as any).photoFile || (filteredGallery[lightboxIndex] as any).photo ? (
              <img src={safeUrl((filteredGallery[lightboxIndex] as any).photoFile || (filteredGallery[lightboxIndex] as any).photo)} alt={(filteredGallery[lightboxIndex] as any).name || "Gallery"} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon size={80} /></div>
            )}
          </div>
          <div className="absolute bottom-0 left-0 right-0 z-40 bg-white border-t border-black/5 px-10 py-6 flex justify-between items-center">
            <div>
              <p className="text-[10px] font-bold text-[var(--color-secondary)] uppercase tracking-[0.3em] mb-1">{(filteredGallery[lightboxIndex] as any).categoryId || (filteredGallery[lightboxIndex] as any).category}</p>
              <h3 className="font-medium tracking-tighter text-xl italic">{(filteredGallery[lightboxIndex] as any).name || "Untitled"}</h3>
            </div>
            <div className="flex items-center gap-4">
              {lightboxIndex > 0 && (
                <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }} className="text-[10px] font-bold uppercase tracking-widest border border-black/10 px-5 py-2.5 hover:bg-black hover:text-white transition-all flex items-center gap-2">
                  <ChevronLeft size={13} /> Prev
                </button>
              )}
              {lightboxIndex < filteredGallery.length - 1 && (
                <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }} className="text-[10px] font-bold uppercase tracking-widest border border-black/10 px-5 py-2.5 hover:bg-black hover:text-white transition-all flex items-center gap-2">
                  Next <ChevronRight size={13} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinimalistAir;
